import { useTranslation } from "react-i18next";
import { ErrorBox, Skeleton } from "../../components/ui";
import { useLifecycleBoard } from "../../lib/api";
import { SectionCard, Stat } from "../labour/shared";

const KIND_CLS: Record<string, string> = {
  promotion: "bg-green-50 text-status-green",
  transfer: "bg-blue-50 text-brand-primary",
};

/** Promotions and transfers (HRX-06).
 *
 * Both doctypes held zero rows on a workforce across four grades and thirteen companies, so a career here was
 * a current state with no history behind it — you could see that somebody is a Supervisor and never that they
 * became one. Promotions and transfers are shown as one history because from the person's side they are one
 * thing: their job changed.
 *
 * A move that changes nothing is refused at the server rather than recorded, since an empty event in a career
 * history is worse than no event: it looks like something happened. */
export default function Movements({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useLifecycleBoard(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6 grid gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>;
  const d = q.data;

  return (
    <div className="mt-4 space-y-4" data-hr-movements>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-hr-movements-strip>
        <Stat label={t("evt.moves")} value={d.movement_summary.total} hint={t("evt.movesHint", { n: d.movement_summary.employees })} />
        <Stat label={t("evt.promotions")} value={d.movement_summary.promotions} cls="text-status-green" />
        <Stat label={t("evt.transfers")} value={d.movement_summary.transfers} cls="text-brand-primary" />
        <Stat label={t("evt.noticeDays")} value={d.notice_days} hint={t("evt.noticeHint")} />
      </div>

      <SectionCard title={t("evt.history")} aside={t("evt.historyN", { n: d.movements.length })}>
        {d.movements.length === 0 ? (
          <div className="p-6 text-center text-sm text-brand-grey" data-hr-movements-empty>{t("evt.noMoves")}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F6F8FA] text-left text-[11px] uppercase tracking-wide text-brand-grey">
              <tr>
                <th className="px-4 py-2">{t("wfa.person")}</th>
                <th className="px-2 py-2">{t("evt.when")}</th>
                <th className="px-2 py-2">{t("evt.kind")}</th>
                <th className="px-4 py-2">{t("evt.changed")}</th>
              </tr>
            </thead>
            <tbody>
              {d.movements.map((m) => (
                <tr key={m.name} className="border-t border-brand-grey-light" data-hr-movement={m.name}>
                  <td className="px-4 py-2">
                    <div className="font-medium">{m.employee_name}</div>
                    <div className="text-[11px] text-brand-grey">{m.company}</div>
                  </td>
                  <td className="px-2 py-2 tabular-nums">{m.date}</td>
                  <td className="px-2 py-2">
                    <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${KIND_CLS[m.kind] ?? ""}`}>
                      {t(`evt.kinds.${m.kind}`)}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {m.changes.length === 0 ? <span className="text-brand-grey">—</span> : (
                      <ul className="space-y-0.5">
                        {m.changes.map((c, i) => (
                          <li key={`${m.name}-${i}`} className="text-[12px]">
                            <span className="text-brand-grey">{c.field}:</span>{" "}
                            <span className="line-through decoration-brand-grey/50">{c.from || "—"}</span>{" → "}
                            <span className="font-semibold">{c.to}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
}
